import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Circle, MinusCircle, Rocket, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';

import { apiDetail } from '../lib/apiError';
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BILL-10 — Getting Started. Company setup → properties → imports →
 * accounting → bank → first report → operations, each marked done because
 * the thing exists in the company's data. Skippable steps can be skipped;
 * the card can be dismissed; it disappears once every step is done.
 */
export default function GettingStartedCard() {
  const { token, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const canEdit = hasPermission ? hasPermission('settings:edit') : false;
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/onboarding`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setState(r.data))
      .catch(() => setState(null));
  }, [token]);

  const post = async (url, body) => {
    try {
      const r = await axios.post(`${API_URL}${url}`, body || {}, { headers });
      setState(r.data);
    } catch (e) {
      toast.error(apiDetail(e) || 'Could not update Getting Started');
    }
  };

  if (!state || state.complete) return null;
  // TEAM-2 — dismissing hid it for the whole company with no way back.
  if (state.dismissed) {
    return canEdit ? (
      <p className="text-xs text-slate-600 dark:text-slate-300" data-testid="getting-started-hidden">
        Getting Started is hidden ({state.finished} of {state.total} steps).{' '}
        <button type="button" onClick={() => post('/api/onboarding/dismiss', { dismissed: false })}
          className="underline text-cyan-700 dark:text-cyan-400" data-testid="show-getting-started">
          Show
        </button>
      </p>
    ) : null;
  }

  const pct = Math.round((state.finished / state.total) * 100);

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5" data-testid="getting-started-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Rocket className="w-5 h-5 text-cyan-700 dark:text-cyan-400" />
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Getting Started</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="getting-started-count">
              {state.finished} of {state.total} steps
            </p>
          </div>
        </div>
        {canEdit && (
          <button type="button" onClick={() => post('/api/onboarding/dismiss', { dismissed: true })}
            className="text-slate-400 hover:text-slate-600" aria-label="Dismiss Getting Started"
            data-testid="dismiss-getting-started">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
        <div className="h-full bg-cyan-700" style={{ width: `${pct}%` }} />
      </div>
      <ol className="mt-4 space-y-2">
        {state.steps.map(step => (
          <li key={step.key} className="flex items-start gap-3" data-testid={`onboarding-step-${step.key}`}>
            {step.status === 'done' && <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />}
            {step.status === 'skipped' && <MinusCircle className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />}
            {step.status === 'todo' && <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${step.status === 'todo' ? 'text-slate-900 dark:text-white' : 'text-slate-500 line-through'}`}>
                {step.title}
              </p>
              {step.status === 'todo' && (
                <p className="text-xs text-slate-500 dark:text-slate-400">{step.detail}</p>
              )}
            </div>
            {step.status === 'todo' && (
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant={state.next?.key === step.key ? 'default' : 'outline'}
                  onClick={() => navigate(step.path)} data-testid={`onboarding-go-${step.key}`}>
                  {step.action}
                </Button>
                {step.skippable && canEdit && (
                  <Button size="sm" variant="ghost" onClick={() => post(`/api/onboarding/steps/${step.key}/skip`)}
                    data-testid={`onboarding-skip-${step.key}`}>
                    Skip
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
